import React, { useEffect, useCallback, useState } from 'react';
import { Loader2, AlertCircle, BookOpen, Zap, Play, Pause, Plus } from 'lucide-react';
import { GlassCard } from '../shared';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { adminApi } from '../../services/api';

const GlossesToolsCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedMode, setSelectedMode] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [logs, setLogs] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await adminApi.glosses.status();
      setStatus(r.data);
      setLoading(false);

      // Add to logs if status changed
      if (r.data?.last_operation) {
        const newLog = {
          timestamp: new Date().toLocaleTimeString('fr-FR'),
          message: r.data.last_operation.message,
          status: r.data.last_operation.status,
        };
        setLogs((prev) => [newLog, ...prev.slice(0, 19)]);
      }
    } catch (e) {
      console.error('glosses status fetch failed', e);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    if (!autoRefresh) return;
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [fetchStatus, autoRefresh]);

  const handleGenerate = async (mode) => {
    setIsGenerating(true);
    try {
      const res = await adminApi.glosses.generate(mode);
      if (res.data.success) {
        setLogs((prev) => [
          {
            timestamp: new Date().toLocaleTimeString('fr-FR'),
            message: `Démarrage de la génération (${mode})...`,
            status: 'running',
          },
          ...prev,
        ]);
        setShowDialog(false);
        // Start auto-refresh
        setAutoRefresh(true);
      } else {
        setLogs((prev) => [
          {
            timestamp: new Date().toLocaleTimeString('fr-FR'),
            message: `❌ ${res.data.message}`,
            status: 'error',
          },
          ...prev,
        ]);
      }
    } catch (e) {
      console.error('Generate failed:', e);
      setLogs((prev) => [
        {
          timestamp: new Date().toLocaleTimeString('fr-FR'),
          message: `❌ Erreur: ${e.message}`,
          status: 'error',
        },
        ...prev,
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const counts = status?.counts || {};
  const operation = status?.last_operation;
  const isRunning = operation?.status === 'running';
  const openaiConfigured = status?.openai_configured;

  return (
    <div className="space-y-4">
      <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium">
        Génération de Définitions (OpenAI)
      </h2>

      {/* Config warning */}
      {!openaiConfigured && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-900 dark:text-amber-200 font-medium">
              OpenAI API non configurée
            </p>
            <p className="text-xs text-amber-900/70 dark:text-amber-200/70 mt-1">
              Ajoute `OPENAI_API_KEY` dans le fichier `.env` pour utiliser la génération de définitions.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <GlassCard className="p-3">
          <div className="text-xs text-text-muted">Lemmas</div>
          <div className="text-xl font-bold text-text-primary">
            {counts.lemmas_with_definitions || 0}
          </div>
          <div className="text-xs text-text-muted">
            / {counts.lemmas_total || 0}
          </div>
        </GlassCard>

        <GlassCard className="p-3">
          <div className="text-xs text-text-muted">Racines</div>
          <div className="text-xl font-bold text-text-primary">
            {counts.roots_with_definitions || 0}
          </div>
          <div className="text-xs text-text-muted">
            / {counts.roots_total || 0}
          </div>
        </GlassCard>

        <GlassCard className="p-3">
          <div className="text-xs text-text-muted">Glosses FR</div>
          <div className="text-xl font-bold text-text-primary">
            {counts.glosses_fr || 0}
          </div>
          <div className="text-xs text-text-muted">
            EN: {counts.glosses_en || 0}
          </div>
        </GlassCard>
      </div>

      {/* Status */}
      {operation && (
        <GlassCard className={`p-3 border ${
          operation.status === 'running'
            ? 'border-blue-500/30'
            : operation.status === 'ok'
            ? 'border-emerald-500/30'
            : 'border-rose-500/30'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-text-muted">
              Dernière opération
            </span>
            <span
              className={`text-xs font-medium px-2 py-1 rounded ${
                operation.status === 'running'
                  ? 'bg-blue-500/20 text-blue-400'
                  : operation.status === 'ok'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-rose-500/20 text-rose-400'
              }`}
            >
              {operation.status === 'running' && 'En cours...'}
              {operation.status === 'ok' && '✓ Réussi'}
              {operation.status === 'failed' && '✗ Échoué'}
            </span>
          </div>
          <p className="text-sm text-text-secondary">{operation.message}</p>
          {operation.timestamp && (
            <p className="text-xs text-text-muted mt-2">
              {new Date(operation.timestamp).toLocaleString('fr-FR')}
            </p>
          )}
        </GlassCard>
      )}

      {/* Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setShowDialog(true)}
          disabled={!openaiConfigured || isRunning || loading}
          className="bg-primary text-background"
          size="sm"
        >
          {isRunning ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Zap className="w-3.5 h-3.5 mr-1" />
              Générer définitions
            </>
          )}
        </Button>

        <Button
          onClick={fetchStatus}
          variant="outline"
          size="sm"
          disabled={loading}
        >
          <BookOpen className="w-3.5 h-3.5 mr-1" />
          Actualiser
        </Button>

        <Button
          onClick={() => setAutoRefresh(!autoRefresh)}
          variant={autoRefresh ? 'default' : 'outline'}
          size="sm"
        >
          {autoRefresh ? (
            <>
              <Pause className="w-3.5 h-3.5 mr-1" />
              Auto (3s)
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5 mr-1" />
              Manuel
            </>
          )}
        </Button>
      </div>

      {/* Dialog - Select mode */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Générer les définitions</DialogTitle>
            <DialogDescription>
              Sélectionne le type de mots à traiter avec OpenAI GPT-4
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-primary/5"
                   onClick={() => setSelectedMode('lemma')}>
              <input
                type="radio"
                name="mode"
                value="lemma"
                checked={selectedMode === 'lemma'}
                onChange={() => setSelectedMode('lemma')}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-sm">Lemmas uniquement</div>
                <div className="text-xs text-text-muted">Plus rapide (~{Math.round(counts.lemmas_total / 2)} définitions)</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-primary/5"
                   onClick={() => setSelectedMode('root')}>
              <input
                type="radio"
                name="mode"
                value="root"
                checked={selectedMode === 'root'}
                onChange={() => setSelectedMode('root')}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-sm">Racines uniquement</div>
                <div className="text-xs text-text-muted">Recommandé (~{Math.round(counts.roots_total / 2)} définitions)</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-primary/5 border-primary/50"
                   onClick={() => setSelectedMode('all')}>
              <input
                type="radio"
                name="mode"
                value="all"
                checked={selectedMode === 'all'}
                onChange={() => setSelectedMode('all')}
                className="w-4 h-4"
              />
              <div>
                <div className="font-medium text-sm">Lemmas + Racines</div>
                <div className="text-xs text-text-muted">Complet (~{Math.round((counts.lemmas_total || 0) / 2 + (counts.roots_total || 0) / 2)} définitions)</div>
              </div>
            </label>
          </div>

          <div className="flex items-start gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/30">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 dark:text-amber-200">
              ⚠ Coût estimé: ~0.30€ pour le corpus complet. Chaque définition passe par GPT-4.
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowDialog(false)} variant="outline">
              Annuler
            </Button>
            <Button
              onClick={() => handleGenerate(selectedMode)}
              disabled={isGenerating}
              className="bg-primary text-background"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  Démarrage...
                </>
              ) : (
                <>
                  <Zap className="w-3.5 h-3.5 mr-1" />
                  Générer ({selectedMode})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs */}
      {logs.length > 0 && (
        <GlassCard className="p-3">
          <div className="text-xs uppercase tracking-wider text-text-muted mb-2 font-medium">
            Historique
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="text-xs font-mono text-text-muted flex gap-2">
                <span className="shrink-0">[{log.timestamp}]</span>
                <span className="line-clamp-1">{log.message}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default GlossesToolsCard;
